import { useEffect, useRef, useState } from 'react'
import type { CrashNotice, CrashNoticeId, CrashNoticeTone } from './useCrashChipBetting'

// One dwell for the transient slot — matches the reference chip-panel
// notice (~2.5s warning dwell).
export const CRASH_NOTICE_DWELL_MS = 2500

type Translate = (key: string) => string

/**
 * The literal copy for each abstract notice id (v12.4 ask 7). Literal t()
 * calls only — the i18n extractor reads these (the hook itself fires ids so
 * it stays i18n-free and unit-testable). Copy mirrors the reference panels
 * where a counterpart exists (ChipPlacementPanel / PlaceBetPanel toasts).
 */
export function crashNoticeText(id: CrashNoticeId, t: Translate): string {
  switch (id) {
    case 'cashout-locked-round':
      return t('Auto cash-out is locked for this round')
    case 'cashout-off-next-round':
      return t('Auto cash-out stays on this round — off from next round')
    case 'cashout-on-next-round':
      return t('Auto cash-out re-enabled for next round')
    case 'max-reached':
      return t('Max bet reached')
    case 'insufficient-balance':
      return t('Insufficient balance')
    case 'balance-initializing':
      return t('Initializing balance')
    case 'insufficient-balance-auto-stopped':
      return t('Insufficient balance — auto-bet stopped')
    case 'insufficient-balance-not-placed':
      return t('Insufficient balance — bet not placed')
    case 'below-minimum-not-placed':
      return t('Below minimum bet — bet not placed')
    case 'stake-reduced':
      return t('Insufficient balance — stake reduced')
    case 'auto-stopped-edit':
      return t('Auto-bet stopped — bet modified')
    case 'submit-aborted':
      return t('Could not place — holding for next round')
    case 'bet-failed':
      return t('Bet did not go through')
    case 'cashout-failed':
      return t('Cash out failed — please try again')
    case 'cashout-just-missed':
      return t('Just missed — the round crashed')
    case 'session-expired':
      return t('Session expired')
  }
}

export interface CrashNoticeDisplay {
  text: string
  tone: CrashNoticeTone
  // Render key — changes on EVERY fire so React remounts the pill and the
  // entrance animation re-fires even for duplicate messages (G13).
  key: string
}

// The slice of the ui-store toast the bridge consumes (kept structural so
// the hook is testable without the store).
export interface ToastLike {
  show: boolean
  message?: string
  type?: 'success' | 'error' | 'warning'
}

/**
 * The crash panel's ONE transient notice display (v12.4 ask 7, G13):
 *
 *   - source 1: the betting hook's abstract notice (placement refusals,
 *     submit trims/drops, transport aborts, cash-out failures) — mapped to
 *     literal t() copy here;
 *   - source 2: the global ui-store toast — the channel handleBetRejected
 *     pushes the MAPPED rejection reason through (store/ui betRejected;
 *     PlaceBetPanel renders it as its Toast — crash pages had no renderer,
 *     G6/G7). The toast keys already exist in every locale (Toast.tsx does
 *     t(message)); the bridge consumes-and-clears so the message can
 *     re-fire. Declared AFTER the hook-notice effect, so when a rejection
 *     lands both sources in one commit the specific reason wins the slot.
 *
 * Replace-not-stack, ~2.5s dwell, re-keyed per fire.
 */
export function useCrashNoticeDisplay(
  notice: CrashNotice | null,
  toast: ToastLike,
  clearToast: () => void,
  t: Translate,
  dwellMs: number = CRASH_NOTICE_DWELL_MS
): CrashNoticeDisplay | null {
  const [display, setDisplay] = useState<CrashNoticeDisplay | null>(null)

  // Hook-notice source. seq-guarded: t()'s identity can churn per render
  // and an already-consumed notice must never re-display (e.g. after its
  // dwell expired).
  const lastSeqRef = useRef(0)
  useEffect(() => {
    if (!notice || notice.seq === lastSeqRef.current) return
    lastSeqRef.current = notice.seq
    setDisplay({
      text: crashNoticeText(notice.id, t),
      tone: notice.tone,
      key: `n${notice.seq}`
    })
  }, [notice, t])

  // ui-toast bridge. Consume-and-clear; keyed by its own counter.
  const toastSeqRef = useRef(0)
  useEffect(() => {
    if (!toast.show || !toast.message) return
    toastSeqRef.current += 1
    setDisplay({
      text: t(toast.message),
      tone: toast.type === 'success' ? 'success' : 'warning',
      key: `t${toastSeqRef.current}`
    })
    clearToast()
  }, [toast, clearToast, t])

  // Dwell — each new display restarts the timer (its identity changed).
  useEffect(() => {
    if (!display) return undefined
    const id = setTimeout(() => setDisplay(null), dwellMs)
    return () => clearTimeout(id)
  }, [display, dwellMs])

  return display
}
